/**
 *  [2023] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * 組織単位で案件を管理するDBの使い方をサポートするキャッシュ機能
 *  caseBasedDataManagementを拡張する
 */

import {MultiOrgCaseDataManage} from './multiOrgCaseBasedDataManageClass';
import {
  createRecordProc,
  deleteRecordProc,
  getAllRecordsProc,
  updateRecordProc,
  readRecordsByTime2Proc,
  getMediaOfRecordProc,
  putMediaOfRecordProc,
  deleteMediaOfRecordProc,
  bulkCreateRecordProc, bulkUpdateRecordProc, bulkPutMediaOfRecordProc,
  createCaseProc, updateCaseProc, deleteCaseProc,
  createOrganizationProc, updateOrganizationProc, deleteOrganizationProc,
  readInOrganizationProc, readOrganizationsProc,
  flushForTesting, archiveForTesting, forceDeleteCaseForTesting, rawPutProc,
} from './wrappers';
import {sleep} from '@zettant/commonUtils/utils';
import {init} from './multiOrgCaseBasedDataManageInitializer';

const MULTI_CASE_DATA_ADMIN_GROUP = '##_ADMGRP_##';

const cacheTable: {[key:string]: MultiOrgCaseDataManage} = {};


/**
 * IndexedDb(CacheManagemオブジェクト)を準備する
 *
 * @param tableName
 * @param email
 * @param adapters
 * @param localStorageMode
 */
const prepareTable = async (tableName: string, email?:string, adapters?: any, localStorageMode=false): Promise<MultiOrgCaseDataManage> => {
  if (!(tableName in cacheTable)) {
    const tbl = await init(tableName, email, localStorageMode);
    if (!adapters) {
      tbl.setup({
        createAdapter: createRecordProc,
        updateAdapter: updateRecordProc,
        bulkCreateAdapter: bulkCreateRecordProc,
        bulkUpdateAdapter: bulkUpdateRecordProc,
        deleteAdapter: deleteRecordProc,
        readAdapter2: readRecordsByTime2Proc,
        readAllAdapter: getAllRecordsProc,
        putMediaAdapter: putMediaOfRecordProc,
        bulkPutMediaAdapter: bulkPutMediaOfRecordProc,
        getMediaAdapter: getMediaOfRecordProc,
        deleteMediaAdapter: deleteMediaOfRecordProc,
        createCaseAdapter: createCaseProc,
        updateCaseAdapter: updateCaseProc,
        deleteCaseAdapter: deleteCaseProc,
        createOrganizationAdapter: createOrganizationProc,
        updateOrganizationAdapter: updateOrganizationProc,
        deleteOrganizationAdapter: deleteOrganizationProc,
        readInOrganizationAdapter: readInOrganizationProc,
        readOrganizationsAdapter: readOrganizationsProc,
        archiveForTestingAdapter: archiveForTesting,
        forceDeleteCaseForTestingAdapter: forceDeleteCaseForTesting,
        rawPutAdapter: rawPutProc,
        flushForTestingAdapter: flushForTesting,
      });
    } else {
      tbl.setup({
        createAdapter: adapters.createAdapter,
        updateAdapter: adapters.updateAdapter,
        bulkCreateAdapter: adapters.bulkCreateAdapter,
        bulkUpdateAdapter: adapters.bulkUpdateAdapter,
        deleteAdapter: adapters.deleteAdapter,
        readAdapter2: adapters.readAdapter2,
        readAllAdapter: adapters.readAllAdapter,
        putMediaAdapter: adapters.putMediaAdapter,
        bulkPutMediaAdapter: adapters.bulkPutMediaAdapter,
        getMediaAdapter: adapters.getMediaAdapter,
        deleteMediaAdapter: adapters.deleteMediaAdapter,
        createCaseAdapter: adapters.createCaseAdapter,
        updateCaseAdapter: adapters.updateCaseAdapter,
        deleteCaseAdapter: adapters.deleteCaseAdapter,
        createOrganizationAdapter: adapters.createOrganizationAdapter,
        updateOrganizationAdapter: adapters.updateOrganizationAdapter,
        deleteOrganizationAdapter: adapters.deleteOrganizationAdapter,
        readInOrganizationAdapter: adapters.readInOrganizationAdapter,
        readOrganizationsAdapter: adapters.readOrganizationsAdapter,
        archiveForTestingAdapter: adapters.archiveForTestingAdapter,
        forceDeleteCaseForTestingAdapter: adapters.forceDeleteCaseForTestingAdapter,
        rawPutAdapter: adapters.rawPutAdapter,
        flushForTestingAdapter: adapters.flushForTestingAdapter,
      });
    }
    cacheTable[tableName] = tbl;
  }
  return cacheTable[tableName];
};


/**
 * テーブル用のキャッシュオブジェクトを取得する。存在しなければprepareが完了するまで待つ。
 *
 * @param tableName
 */
const getTable = async (tableName: string): Promise<MultiOrgCaseDataManage> => {
  let counter = 100;
  do {
    const tbl = cacheTable[tableName];
    if (tbl) return tbl;
    await sleep(50); // 50ms待ってみる
    counter -= 1;
  } while (counter > 0);
  if (typeof window !== 'undefined') {
    window.alert('cacheManageが初期化できていません');
  }
  return {} as MultiOrgCaseDataManage;
};

const getTableSync = (tableName: string): MultiOrgCaseDataManage => cacheTable[tableName];

const getAllTables = () => cacheTable;

const removeTable = (tableName: string) => {
  if (tableName in cacheTable) {
    delete cacheTable[tableName];
  }
};

export {getAllTables, getTableSync, prepareTable, getTable, removeTable};
export {MULTI_CASE_DATA_ADMIN_GROUP};
